import { Component, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FormGroupTypeSafe } from 'angular-typesafe-reactive-forms-helper';
import {
  BasisOfNotice,
  CreateSefObavestenjeCommand,
  DocumentIssueOrigin,
  DocumentReferenceType,
  ICreateSefObavestenjeCommand,
  IRacunZaSefObavestenjeDto,
  PdvEvidencijeClient,
  TipRacuna,
} from '@kodit/core/data-api';
import { FormHelper, SefObavestenjeFormService } from '@kodit/core/form-definitions';
import { SefObavestenjeService } from '../sef-obavestenje.service';
import { AlertService, LocationService, SharedService } from '@kodit/core/services';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'kodit-obavestenje-form',
  templateUrl: './obavestenje-form.component.html',
  styleUrls: ['./obavestenje-form.component.scss'],
})
export class ObavestenjeFormComponent implements OnInit, OnDestroy {
  private _subs = new Subscription();
  private _searchSubject = new Subject<string>();

  form!: FormGroupTypeSafe<ICreateSefObavestenjeCommand>;
  racunSuggestions: IRacunZaSefObavestenjeDto[] = [];
  racunSearchValue: IRacunZaSefObavestenjeDto | string = '';

  porekeloDokumentaOptions: SelectItem[] = [];
  osnovObavestenjaOptions: SelectItem[] = [];
  tipReferentnogDokumentaOptions: SelectItem[] = [];

  private _tipOpcijePoCreditNote: SelectItem[] = [];
  private _tipOpcijePoStorno: SelectItem[] = [];
  private _returnUrl: string | null = null;

  get jePeriodTip(): boolean {
    return this.form?.controls.tipReferentnogDokumenta?.value === DocumentReferenceType.CREDIT_NOTE_REFERENCE_TO_PERIOD;
  }

  get jeStornoOsnov(): boolean {
    return this.form?.controls.osnovObavestenja?.value === BasisOfNotice.STORNO;
  }

  constructor(
    private _formService: SefObavestenjeFormService,
    private _sefObavestenjeService: SefObavestenjeService,
    private _client: PdvEvidencijeClient,
    private _alert: AlertService,
    private _locationService: LocationService,
    private _sharedService: SharedService,
    private _confirmationService: ConfirmationService,
    private _formHelper: FormHelper,
    private _router: Router
  ) {
    const nav = this._router.getCurrentNavigation();
    this._returnUrl = nav?.extras?.state?.['returnUrl'] ?? null;
  }

  ngOnInit(): void {
    this.form = this._formService.GetSefObavestenjeFormGroup();
    this._loadDropdownOptions();

    this._subs.add(
      (this.form.controls.tipReferentnogDokumenta as any).valueChanges.subscribe((tip: number) => {
        this._updatePeriodValidators(tip);
      })
    );

    this._subs.add(
      (this.form.controls.osnovObavestenja as any).valueChanges.subscribe((osnov: number) => {
        const stornoCtrl = this.form.controls.datumStornoReferentnogDokumenta as any;
        const tipCtrl = this.form.controls.tipReferentnogDokumenta as any;

        if (osnov === BasisOfNotice.STORNO) {
          stornoCtrl.setValidators(Validators.required);
          this.tipReferentnogDokumentaOptions = this._tipOpcijePoStorno;
          tipCtrl.setValue(this._tipOpcijePoStorno[0].value);
        } else {
          stornoCtrl.clearValidators();
          stornoCtrl.setValue(null);
          this.tipReferentnogDokumentaOptions = this._tipOpcijePoCreditNote;
          tipCtrl.setValue(this._tipOpcijePoCreditNote[0].value);
        }
        stornoCtrl.updateValueAndValidity();
      })
    );

    this._subs.add(
      this._searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => this._client.getRacuniForSefObavestenjeAutocomplete(term))
      ).subscribe((res) => {
        this.racunSuggestions = res?.data ?? [];
      })
    );

    this._subs.add(
      this._sefObavestenjeService.preSelectedRacun$.subscribe((racun) => {
        if (racun) {
          this.racunSearchValue = racun;
          this.handleRacunSelect(racun);
          this._sefObavestenjeService.setPreSelectedRacun(null);
        }
      })
    );
  }

  private _loadDropdownOptions(): void {
    this.porekeloDokumentaOptions = [
      { label: 'Dokument izdat putem SEF-a', value: DocumentIssueOrigin.DOCUMENT_ISSUED_VIA_SEF },
      { label: 'Dokument izdat van SEF-a', value: DocumentIssueOrigin.DOCUMENT_ISSUED_OUTSIDE_SEF },
    ];

    this.osnovObavestenjaOptions = [
      { label: 'Dokument o smanjenju', value: BasisOfNotice.CREDIT_NOTE_ISSUED },
      { label: 'Storno', value: BasisOfNotice.STORNO },
    ];

    this._tipOpcijePoCreditNote = [
      { label: 'Dokument o smanjenju koji se odnosi na fakturu za promet', value: DocumentReferenceType.CREDIT_NOTE_REFERENCE_TO_INVOICE },
      { label: 'Dokument o smanjenju koji se odnosi na avansnu fakturu', value: DocumentReferenceType.CREDIT_NOTE_REFERENCE_TO_PREPAYMENT_INVOICE },
      { label: 'Dokument o smanjenju koji se odnosi na vremenski period u kojem su izdate fakture za promet', value: DocumentReferenceType.CREDIT_NOTE_REFERENCE_TO_PERIOD },
    ];

    this._tipOpcijePoStorno = [
      { label: 'Storno fakture za promet', value: DocumentReferenceType.STORNO_INVOICE },
      { label: 'Storno avansne fakture', value: DocumentReferenceType.STORNO_PREPAYMENT },
      { label: 'Storno dokumenta o povećanju', value: DocumentReferenceType.STORNO_DEBIT_NOTE },
    ];

    this.tipReferentnogDokumentaOptions = this._tipOpcijePoCreditNote;
  }

  handleRacunSearch(event: { query: string }): void {
    if (event.query?.length >= 1) {
      this._searchSubject.next(event.query);
    } else {
      this.racunSuggestions = [];
    }
  }

  handleRacunSelect(racun: IRacunZaSefObavestenjeDto): void {
    (this.form.controls.brojReferentnogDokumenta as any).setValue(racun.broj ?? '');
    (this.form.controls.datumReferentnogDokumenta as any).setValue(racun.datumIzdavanja ?? undefined);
    (this.form.controls.pibPrimaoca as any).setValue(racun.pib ?? '');
    (this.form.controls.mbPrimaoca as any).setValue(racun.maticniBroj ?? '');
    (this.form.controls.ukupanIznosPdv as any).setValue(racun.ukupanIznosPdv ?? 0);

    const jeKnjiznoOdobrenje = racun.tip === TipRacuna.DOKUMENT_O_SMANJENJU;

    if (jeKnjiznoOdobrenje) {
      (this.form.controls.osnovObavestenja as any).setValue(BasisOfNotice.CREDIT_NOTE_ISSUED);
      if (racun.jeReferencaNaAvansniRacun === true) {
        (this.form.controls.tipReferentnogDokumenta as any).setValue(DocumentReferenceType.CREDIT_NOTE_REFERENCE_TO_PREPAYMENT_INVOICE);
      } else if (racun.jeReferencaNaAvansniRacun === false) {
        (this.form.controls.tipReferentnogDokumenta as any).setValue(DocumentReferenceType.CREDIT_NOTE_REFERENCE_TO_INVOICE);
      } else if (racun.periodOd && racun.periodDo) {
        (this.form.controls.tipReferentnogDokumenta as any).setValue(DocumentReferenceType.CREDIT_NOTE_REFERENCE_TO_PERIOD);
        (this.form.controls.pocetakPeriodaFakture as any).setValue(racun.periodOd);
        (this.form.controls.krajPeriodaFakture as any).setValue(racun.periodDo);
      }
    } else if (racun.jeStorno) {
      (this.form.controls.osnovObavestenja as any).setValue(BasisOfNotice.STORNO);
      if (racun.datumStorniranja) {
        (this.form.controls.datumStornoReferentnogDokumenta as any).setValue(racun.datumStorniranja);
      } else {
        this._subs.add(
          this._client.getRacunForSefObavestenjeById(racun.id!, racun.jeNeobradjeniSefRacun ?? false).subscribe((res) => {
            if (res?.data?.datumStorniranja) {
              (this.form.controls.datumStornoReferentnogDokumenta as any).setValue(res.data.datumStorniranja);
            }
          })
        );
      }
      (this.form.controls.tipReferentnogDokumenta as any).setValue(this._mapTipRacunaToTipReferentnogDokumenta(racun.tip ?? TipRacuna.EMPTY));
    }
  }

  onRacunInputKeyUp(): void {
    if (!this.racunSearchValue) {
      this.handleRacunClear();
    }
  }

  handleRacunClear(): void {
    (this.form.controls.brojReferentnogDokumenta as any).setValue(undefined);
    (this.form.controls.datumReferentnogDokumenta as any).setValue(undefined);
    (this.form.controls.pibPrimaoca as any).setValue(undefined);
    this.racunSearchValue = '';
  }

  getRacunLabel(racun: IRacunZaSefObavestenjeDto): string {
    return racun?.broj ?? '';
  }

  private _updatePeriodValidators(tip: number): void {
    const pocetakCtrl = this.form.controls.pocetakPeriodaFakture as any;
    const krajCtrl = this.form.controls.krajPeriodaFakture as any;
    if (tip === DocumentReferenceType.CREDIT_NOTE_REFERENCE_TO_PERIOD) {
      pocetakCtrl.setValidators(Validators.required);
      krajCtrl.setValidators(Validators.required);
    } else {
      pocetakCtrl.clearValidators();
      pocetakCtrl.setValue(null);
      krajCtrl.clearValidators();
      krajCtrl.setValue(null);
    }
    pocetakCtrl.updateValueAndValidity();
    krajCtrl.updateValueAndValidity();
  }

  private _mapTipRacunaToTipReferentnogDokumenta(tip: TipRacuna): DocumentReferenceType {
    switch (tip) {
      case TipRacuna.AVANSNI_RACUN: return DocumentReferenceType.STORNO_PREPAYMENT;
      case TipRacuna.DOKUMENT_O_POVECANJU: return DocumentReferenceType.STORNO_DEBIT_NOTE;
      default: return DocumentReferenceType.STORNO_INVOICE;
    }
  }

  handleCancel() {
    this._confirmationService.confirm({
      message: 'Ukoliko otkažete sav nesnimljen sadržaj će biti izgubljen.',
      acceptLabel: 'Otkaži',
      rejectLabel: 'Ostani na stranici',
      header: 'Potvrdite otkazivanje',
      icon: 'far fa-check-circle',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        if (this._returnUrl) {
          this._router.navigateByUrl(this._returnUrl);
        } else {
          this._locationService.routeBack();
        }
      },
    });
  }

  handleSave() {
    if (!this.form.valid) {
      this._formHelper.invalidateForm(this.form);
      return;
    }

    this._sharedService.displayLoadingScreen(true, 'Slanje obaveštenja na SEF...');

    const command = new CreateSefObavestenjeCommand(this.form.value as ICreateSefObavestenjeCommand);

    this._subs.add(
      this._client
        .createSefObavestenje(command)
        .pipe(finalize(() => this._sharedService.resetLoadingScreen()))
        .subscribe({
          next: (res) => {
            if (res?.succeeded) {
              this._alert.addSuccessMsg('Obaveštenje je uspešno poslato na SEF.');
              if (this._returnUrl) {
                this._router.navigateByUrl(this._returnUrl);
              } else {
                this._locationService.routeBack();
              }
            } else {
              this._alert.addFailedMsg(res?.messages?.[0] ?? 'Greška pri slanju obaveštenja.');
            }
          }
        })
    );
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
